import React, { useCallback, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { musicServicesByMusicAPIID } from 'fym-shared/src/music-services/allMusicServices';
import { ServiceConnectionStatus } from 'fym-shared/src/smartlinks/catalog';
import { TypedMemo } from '../../common/types';
import { useFetchAccountStatsQuery, useGetArtistPortalOverviewQuery } from '../../../redux/api/fymAPI';
import { usePortalSetupNeeded } from '../navigation/hooks/usePortalSetupNeeded';
import { HomeKpiTile } from './HomeKpiTile';
import { HomeKpiSkeleton } from './HomeKpiSkeleton';
import { HomeKpiSetupTile } from './HomeKpiSetupTile';
import { HomeRowError } from './HomeRowError';
import { StreamsExtraTiles } from './StreamsExtraTiles';
import { artistHomeMessages as messages } from './artistHomeMessages';
import { flowDelta } from './homeDeltas';
import { HomePeriodDays } from './homePeriod';
import { useHomeSmartLinks } from './useHomeSmartLinks';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole Home; each part uses its own slice of it */
import styles from './ArtistHome.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamsRowProps {
  accountId: number;
  days: HomePeriodDays;
  onOpenInsights: () => void;
  onOpenCatalog: () => void;
  onOpenSmartlinks: () => void;
}

const KPI_COLUMNS = 4;

/**
 * The Streams and smartlinks row (design a1): streams and listeners summed over the connected
 * portals, then smartlink views and clicks. With no portal the first two tiles become the
 * "Connect Spotify for Artists" card; with no smartlink the last two are not shown. The Apple Music
 * for Artists Shazams and radio spins follow the listeners when the overview has them.
 */
const StreamsRowComponent: React.FC<StreamsRowProps> = ({
  accountId,
  days,
  onOpenInsights,
  onOpenCatalog,
  onOpenSmartlinks,
}) => {
  const intl = useIntl();
  const isPortalMissing = usePortalSetupNeeded(accountId);
  const overview = useGetArtistPortalOverviewQuery({ accountId, days });
  const stats = useFetchAccountStatsQuery({ accountId, days });
  const smartLinks = useHomeSmartLinks(accountId);

  const portalNames = useMemo(
    () =>
      (overview.data?.services ?? [])
        .filter((service) => service.status === ServiceConnectionStatus.connected)
        .map((service) => musicServicesByMusicAPIID[service.integrationType]?.name ?? service.integrationType)
        .join(', '),
    [overview.data]
  );

  const retry = useCallback(() => {
    overview.refetch().catch(console.error);
    stats.refetch().catch(console.error);
  }, [overview, stats]);

  if (overview.isLoading || stats.isLoading || smartLinks.isLoading) {
    return (
      <div className={styles.kpis}>
        {Array.from({ length: KPI_COLUMNS }, (_, index) => (
          <HomeKpiSkeleton key={index} />
        ))}
      </div>
    );
  }
  const error = stats.error ?? (isPortalMissing ? undefined : overview.error);
  if (error !== undefined) {
    return <HomeRowError error={error} retry={retry} />;
  }

  const hasSmartLinks = (smartLinks.total ?? 0) > 0;
  const combined = overview.data?.combined.totals;
  const totals = stats.data?.stats.totals;
  const previous = stats.data?.previousTotals;

  return (
    <div className={styles.kpis}>
      {isPortalMissing ? (
        <HomeKpiSetupTile
          title={intl.formatMessage(messages.portalsSetupTitle)}
          body={intl.formatMessage(messages.portalsSetupBody)}
          cta={intl.formatMessage(messages.portalsSetupCta)}
          span={hasSmartLinks ? 2 : KPI_COLUMNS}
          isPrimary
          onAction={onOpenCatalog}
        />
      ) : (
        <>
          <HomeKpiTile
            label={intl.formatMessage(messages.kpiStreams)}
            value={combined?.streams.current ?? null}
            delta={flowDelta(intl, combined?.streams.current ?? null, combined?.streams.previous ?? null)}
            source={portalNames}
            onOpen={onOpenInsights}
          />
          <HomeKpiTile
            label={intl.formatMessage(messages.kpiListeners)}
            value={combined?.listeners.current ?? null}
            delta={flowDelta(intl, combined?.listeners.current ?? null, combined?.listeners.previous ?? null)}
            source={portalNames}
            onOpen={onOpenInsights}
          />
          <StreamsExtraTiles overview={overview.data} onOpenInsights={onOpenInsights} />
        </>
      )}
      {hasSmartLinks && totals !== undefined && (
        <>
          <HomeKpiTile
            label={intl.formatMessage(messages.kpiSmartlinkViews)}
            value={totals.views}
            delta={flowDelta(intl, totals.views, previous?.views ?? null)}
            source={intl.formatMessage(messages.sourceLivePages, { count: smartLinks.liveCount })}
            onOpen={onOpenSmartlinks}
          />
          <HomeKpiTile
            label={intl.formatMessage(messages.kpiSmartlinkClicks)}
            dotColor="var(--brand-pink)"
            value={totals.clicks}
            delta={flowDelta(intl, totals.clicks, previous?.clicks ?? null)}
            source={intl.formatMessage(messages.sourceServices, { count: stats.data?.stats.services.length ?? 0 })}
            onOpen={onOpenSmartlinks}
          />
        </>
      )}
    </div>
  );
};

export const StreamsRow = TypedMemo(StreamsRowComponent);
